import { ProvenanceSourceKind } from './clientReference'
import { InferenceReferenceType } from './inference.type'
import { Offset } from './onyx_server.type'

export type Workspace = 'personal' | 'organization' | 'all'
export type Tabs = Workspace | 'connectors' | 'collection'

export interface Ticker {
  stock_id: string
  ticker: string
  name: string
}

// Asset Class Type
export type AssetClassType =
  | 'Equity'
  | 'Fixed Income'
  | 'Rates'
  | 'FX'
  | 'Commodity'
  | 'Index'
  | 'Crypto'
  | 'Economics'

// Storage Collection Types
export interface StorageCollectionFilterMetadata {
  asset_class?: AssetClassType
  tickers?: Ticker[]
  sectors?: string[] // SectorType values
}

export interface StorageCollectionFilterPayload {
  additional_tags?: { asset_class: AssetClassType[] }[]
  tickers?: string[]
  sectors?: { sector: string }[]
}

export interface StorageCollection {
  id: string
  user_id: string
  name: string
  filter_metadata: StorageCollectionFilterMetadata
  filter_payload: StorageCollectionFilterPayload
  created_at: string
  updated_at: string
}

export interface CreateStorageCollectionParams {
  name: string
  filter_metadata: StorageCollectionFilterMetadata
  filter_payload: StorageCollectionFilterPayload
}

export interface UpdateStorageCollectionParams {
  id: string
  name?: string
  filter_metadata?: StorageCollectionFilterMetadata
  filter_payload?: StorageCollectionFilterPayload
}

export enum Connector {
  GoogleDrive = 'googleDrive',
  OneDrive = 'oneDrive',
  SharePoint = 'sharePoint',
  OneNote = 'oneNote',
  eds = 'eds',
  Email = 'email',
  Gmail = 'gmail',
  Outlook = 'outlook',
  s3 = 's3',
  Vault = 'vault',
  LinqDrive = 'linqDrive',
  EchoAgent = 'echoAgent',
  MeetingAgent = 'meetingAgent',
  YouTube = 'youtube',
  Website = 'website',
  ApplePodcast = 'applePodcast',
  CLSA = 'clsa',
  ThirdBridge = 'thirdBridge',
}

export const removedSearchPersonalSources = [Connector.Email, Connector.Gmail]
// export const removedSearchPersonalSources = []
export const removedSearchOrganizationSources = [
  Connector.Email,
  // Connector.Outlook,
  Connector.Gmail,
  // Connector.eds,
] // 임시로 search가 안 되는 소스 제거 처리, 추후 소스 추가 시 제거 필요

export enum ConnectorGroup {
  Drive = 'drive',
  Email = 'email',
  S3 = 's3',
  Note = 'note',
  Vault = 'Vault',
  Web = 'Web',
}

export interface ConnectorState {
  connected: boolean
  email?: string
  metadata?: ConnectorMetadata | null
}

export interface ConnectorMetadata {
  workspace?: Workspace | null
  sync_range_days?: number | null
  sync_hour?: number | null
  last_synced_timestamp?: string | null
}

export interface UpdateMetadataParams {
  connector: Connector
  workspace?: Workspace
  sync_range_days?: number
  sync_hour?: number
  last_synced_timestamp?: string
}

// Personal scope response format (matches current UserConnectors)
export type PersonalConnectorResponse = Record<Connector, ConnectorState>

// Organization scope response format
export interface OrganizationConnectorResponse {
  scope: 'organization'
  organization_id: string
  organization_name: string
  available_sources: string[]
  source_count: number
}

// Union type for both response formats
export type ConnectorResponse = PersonalConnectorResponse | OrganizationConnectorResponse

// Keep the original UserConnectors type for internal use
export type UserConnectors = Record<Connector, ConnectorState>

// TODO: Filters
export interface FilterParams {
  user: UserInfo
  workspace: Workspace
  keyword?: string
  type?: string[]
  date?: string[]
  userIds?: string[]
  tickers?: Ticker[]
  sectors?: string[]
}

export enum StatusType {
  SYNCING = 'Syncing...',
  SYNCING_FAILED = 'Syncing Failed',
  DELETING = 'Deleting...',
  DELETING_FAILED = 'Deleting Failed',
  SYNCED = 'Synced',
}

export enum SectorType {
  BASIC_MATERIALS = 'Basic Materials',
  CONSUMER_CYCLICALS = 'Consumer Cyclicals',
  CONSUMER_STAPLES = 'Consumer Staples',
  ENERGY = 'Energy',
  FINANCIAL_SERVICES = 'Financial Services',
  HEALTHCARE = 'Healthcare',
  INDUSTRIALS = 'Industrials',
  TECHNOLOGY = 'Technology',
  TRANSPORTATION = 'Transportation',
  TELECOM_SERVICES = 'Telecom Services',
  UTILITIES = 'Utilities',
}

export enum ContainerType {
  WORKSPACE = 'workspace',
  FOLDER = 'folder',
  BINDER = 'binder',
  NOTE = 'note',
  PAGE = 'page',
  CHANNEL = 'channel',
  THREAD = 'thread',
  MESSAGE = 'message',
}

export enum DocumentType {
  FILE = 'file',
  EMAIL = 'email',
  MESSAGE = 'message',
  NOTE = 'note',
  ATTACHMENT = 'attachment',
}

export enum ContentType {
  PDF = 'application/pdf',
  DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  PPTX = 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  GOOGLE_DOC = 'application/vnd.google-apps.document',
  GOOGLE_SHEET = 'application/vnd.google-apps.spreadsheet',
  GOOGLE_PRESENTATION = 'application/vnd.google-apps.presentation',
  GOOGLE_FOLDER = 'application/vnd.google-apps.folder',
  SHAREPOINT_FOLDER = 'pseudo/vnd.sharepoint.folder',
  CUSTOM_FOLDER = 'pseudo/vnd.linqalpha.folder',
  OCTET_STREAM = 'application/octet-stream',
  TEXT = 'text/plain',
  MARKDOWN = 'text/markdown',
  AUDIO_MPEG = 'audio/mpeg',
  AUDIO_WAV = 'audio/wav',
  AUDIO_M4A = 'audio/x-m4a',
}

export enum ItemType {
  DOCUMENT = 'document',
  CONTAINER = 'container',
  THREAD = 'thread',
}

export const SearchableStatus = [
  {
    label: 'Syncing',
    value: StatusType.SYNCING,
  },
  {
    label: 'Synced',
    value: StatusType.SYNCED,
  },
  {
    label: 'Syncing Failed',
    value: StatusType.SYNCING_FAILED,
  },
  {
    label: 'Deleting',
    value: StatusType.DELETING,
  },
  {
    label: 'Deleting Failed',
    value: StatusType.DELETING_FAILED,
  },
]

export const ASSET_CLASS_OPTIONS: { label: string; value: AssetClassType }[] = [
  { label: 'Equity', value: 'Equity' },
  { label: 'Fixed Income', value: 'Fixed Income' },
  { label: 'Rates', value: 'Rates' },
  { label: 'FX', value: 'FX' },
  { label: 'Commodity', value: 'Commodity' },
  { label: 'Index', value: 'Index' },
  { label: 'Crypto', value: 'Crypto' },
  { label: 'Economics', value: 'Economics' },
]

export const SearchableSector = [
  {
    label: 'Basic Materials',
    value: SectorType.BASIC_MATERIALS,
  },
  {
    label: 'Consumer Cyclicals',
    value: SectorType.CONSUMER_CYCLICALS,
  },
  {
    label: 'Consumer Staples',
    value: SectorType.CONSUMER_STAPLES,
  },
  {
    label: 'Energy',
    value: SectorType.ENERGY,
  },
  {
    label: 'Financial Services',
    value: SectorType.FINANCIAL_SERVICES,
  },
  {
    label: 'Healthcare',
    value: SectorType.HEALTHCARE,
  },
  {
    label: 'Industrials',
    value: SectorType.INDUSTRIALS,
  },
  {
    label: 'Technology',
    value: SectorType.TECHNOLOGY,
  },
  {
    label: 'Transportation',
    value: SectorType.TRANSPORTATION,
  },
  {
    label: 'Telecom Services',
    value: SectorType.TELECOM_SERVICES,
  },
  {
    label: 'Utilities',
    value: SectorType.UTILITIES,
  },
]

/** Tag placement (server WorkspaceType): `personal` tags are owner-only,
    `organization` tags are visible and editable by every member of the org. */
export type TagWorkspace = 'personal' | 'organization'

/** Custom tag (DB-only model, no color). */
export interface Tag {
  tag_id: string
  name: string
  /** Always present on the tag CRUD endpoints; absent on browse `item.tags`
      (the browse serializer only emits `tag_id`/`name`). */
  workspace?: TagWorkspace
}

export interface FileInfo {
  id: string
  name: string
  contentType: ContentType
  itemType: ItemType
  iconUrl?: string
  sourceIconUrl?: string // For showing Google Drive icon, OneDrive icon, etc.
  url?: string
  parentId?: string
  source: Connector // Identifies the source service
  status?: StatusType
  lastContributionTimeStamp?: string
  syncedTimeStamp?: string
  userId?: string
  userEmail?: string
  username?: string
  organizationId?: string
  organizationName?: string
  workspace?: Workspace
  sectors?: string[]
  error?: string
  content?: string
  tickers?: string[]
  /** Custom tags attached to this item (from browse response, tagged_at ASC). */
  tags?: Tag[]
  rmsId?: string
  itemSubType?: DocumentType | ContainerType
  additional?: Record<string, unknown>
  assetClass?: string // Asset class (ArrowPoint only)
  // S3 URI of the parse target.
  //   - Non-conversion formats: the uploaded file itself (DOCX/PPTX/XLSX/PDF/TXT).
  //     Used by the reference viewer to render the original format via
  //     mammoth/Google Docs/Office Online.
  //   - Conversion-required formats: the conversion output, not the upload —
  //     audio → STT transcript .txt, legacy Office → xlsx/docx/pptx, hwp/hwpx →
  //     PDF. Populated by rms-server once conversion completes, so it also
  //     serves as the readiness signal (see isVaultFileReady).
  originalLink?: string | null
  /** When rms recorded the item (ISO 8601, UTC). The Storage table's Date column and sort key. */
  createdAt?: string
  /** Search only: body passages around the query, HTML-escaped, matches wrapped in <highlight>. */
  snippet?: string | null
}

/** One page of the explore / collection / search listings. */
export interface StoragePage {
  items: FileInfo[]
  /** Absent on the last page. */
  next_cursor?: string | null
}

/** One container on the path returned by `/containers/{id}/ancestors`, top-level first. */
export interface StorageAncestor {
  /** External container_id (e.g. "local_files"). */
  id: string
  rmsId: string
  name: string
  itemSubType: string
  source?: string | null
}

export interface BreadcrumbItem {
  /**
   * Folder id used for the browse API call. When a container has an RMS UUID
   * this is the UUID; otherwise it falls back to the external container_id
   * string. Null for the Storage root.
   */
  id: string | null
  name: string
  /** Present for entries representing a folder/container; omitted for the root. */
  source?: Connector
  /** Container subtype (FOLDER, THREAD, etc.) — used to decide which vault actions are available. */
  itemSubType?: ContainerType | null
  /** RMS UUID of the container, when available. Used as parent for folder creation. */
  rmsId?: string | null
  /**
   * External container_id string (e.g. "local_files", "chat_session",
   * "f_ab12cd34"). Needed to detect which subtree we're in (e.g. local_files
   * vs chat_session), since `id` carries the RMS UUID for browse calls.
   */
  containerId?: string | null
  /**
   * '/'-delimited container path (e.g. "local_files", "local_files/Research").
   * Available for the tail breadcrumb if the current browse response provides
   * it. Used to gate vault-subtree features via prefix matching.
   */
  path?: string | null
  /** False renders the crumb greyed and inert — we hold no id to browse it by. */
  navigable?: boolean
}

export interface SyncReport {
  added: string[]
  updated: string[]
  deleted: string[]
  unchanged: string[]
  errors: string[]
}

// Define window interface to include the Google Picker
declare global {
  interface Window {
    gapi: any
    google: any
  }
}

export interface UserInfo {
  userId: string
  username: string
  userEmail: string
  userRole: string
  organizationId: string
  organizationName: string
}

export interface LightFileInfo {
  id: string
  name: string
  contentType: string
  itemType: ItemType
  source: Connector
  syncedTimeStamp: string
  workspace?: Workspace
  url: string
}

export interface SelectedItemsState {
  [key: string]: {
    item: FileInfo | LightFileInfo
    category: {
      tab: 'personal' | 'organization'
      folder: string | null
      folderName: string
    }
  }
}

export interface RmsV2ProcessThinkParams {
  message_id: string
  think: string
  onyx_chat_message_id: string
}

export interface RmsV2ProcessSubQuestionsParams {
  inference_id: string
  sub_questions: string[]
}

export interface RmsV2ProcessSearchResultParams {
  inference_id: string
  ref_id: string
  reference_type: InferenceReferenceType
  quote: string
  origin_chunk_url: string
  doc_id: string
  title: string
  doc_public_url: string
  s3_file_key: string // 이 데이터로 doc_source_url 생성
  publisher?: string | null
  published_at?: string | null
  offset?:
    | {
        left?: number
        top?: number
        width?: number
        height?: number
        page?: number
        original_page?: number
      }[]
    | null
}

interface BaseRmsV2ProcessExternalSourceParams {
  inference_id: string
  ref_id: string
  title: string
  quote: string
  publisher?: string | null
  published_at?: string | null
}

// News 타입일 때: doc_public_url 필수, doc_id null
interface NewsRmsV2ProcessExternalSourceParams extends BaseRmsV2ProcessExternalSourceParams {
  reference_type: InferenceReferenceType.News
  doc_public_url: string
  doc_id?: null
}

// 다른 타입일 때: doc_id 필수, doc_public_url null
interface OtherRmsV2ProcessExternalSourceParams extends BaseRmsV2ProcessExternalSourceParams {
  reference_type: Exclude<InferenceReferenceType, InferenceReferenceType.News>
  doc_id: string
  doc_public_url?: null
}

// 최종 유니온 타입
export type RmsV2ProcessExternalSourceParams =
  | NewsRmsV2ProcessExternalSourceParams
  | OtherRmsV2ProcessExternalSourceParams

export interface RmsV2CreateAssistantMessageParams {
  message_id: string
  content: string
}

export interface RmsV2Source {
  id: string
  tool_name: string
  citation_content: string
  text: string
  chunk_id: string
  document_id: string
  document_name: string
  external_url: string
  s3_file_key: string // 이 데이터로 doc_source_url 생성
  publisher: string
  published_date: string
  offset: {
    left: number
    top: number
    width: number
    height: number
    page: number
    original_page: number
  }[]
}

export interface InferredMetadata {
  [key: string]: unknown
}

export interface FieldInfo {
  field: string
  order: 'asc' | 'desc'
}

export enum AllowedReferenceExtension {
  PDF = 'pdf',
  HTML = 'html',
  HTM = 'htm',
}

export interface DocumentParams {
  documentId: string
  workspace: Workspace
  source: Connector
  // ``parsed`` returns the parse-target URL (audio transcript / converted
  // modern Office) for preview rendering. Omit (or ``original``) to get the
  // user-facing download URL (vault: supplementary_link first → audio file /
  // raw legacy office).
  linkType?: 'original' | 'parsed'
}

export interface FileExtensionParams {
  inference_reference_id: string
}

/** @deprecated Bucket name is now configured via LINQ_BUCKET_RMS env var */
export enum RmsV2BucketName {
  DOWNLOAD = 'linq-rms',
}

export enum PresignedUrlMode {
  RMS_V2_DOWNLOAD = 'rms_v2_download',
}

export interface RmsV2Reference {
  display?: unknown
  ref_id: string
  idx: number
  onyx_chat_message_id: string | null
  reference_type: InferenceReferenceType
  title: string
  quote: string
  origin_chunk_url: string | null
  doc_id: string | null
  doc_public_url: string | null
  link: string | null
  // agent-server 가 직접 가리키는 S3 object key (`s3://...`). 존재하면
  // reference 는 stock_documents 파이프라인 외부에서 가져온 것이므로
  // session_reference 경로로 라우팅한다.
  s3_file_key?: string | null
  publisher: string | null
  published_at: string | null
  offset?: Offset[] | null
  metadata?: {
    [key: string]: any
  } | null
  custom_metadata?: {
    [key: string]: any
  } | null
  // Boundary adapter (adapters/onyx.ts) 가 deriveProvenanceSourceKind 결과를
  // 스탬프하는 Step-0 파생 필드. 컴포넌트는 raw metadata.source 대신 이 값
  // (또는 boundary 판별자 isStructuredDataReference/isEmailReference) 을 쓴다.
  // adapter 를 거치지 않은 legacy 생성 경로에서는 undefined.
  source_kind?: ProvenanceSourceKind | null
}

export interface EmailMessage {
  id: string
  fileKey: string
  creationTimeStamp: string
}

export interface EmailThreadResponse {
  threadId: string
  lastContributedTimeStamp: string
  messages: EmailMessage[]
  recipients: string[]
  source: Connector.Email | Connector.Outlook | Connector.Gmail
  workspace: string
}

// OneNote notebook type (from Microsoft Graph API)
export interface OneNoteNotebook {
  id: string
  displayName: string
  createdDateTime: string
  lastModifiedDateTime: string
  isDefault: boolean
  isShared: boolean
  links?: {
    oneNoteClientUrl?: { href: string }
    oneNoteWebUrl?: { href: string }
  }
}
